/**
 * anonymizeAuctionHistory.js — strip identity out of a saved league's auction
 * results, leaving only the market data.
 *
 * Pure function. No fetch, no hook imports, no I/O. The caller decides what to
 * do with the payload; this file only decides what may leave the device.
 *
 * The output is a WHITELIST, not a redaction pass. Every emitted field is
 * constructed explicitly below — nothing is spread from the input — so a new
 * key appearing on the saved-league record (sleeperImport/yahooImport keep
 * adding them) cannot leak by default. It has to be added here on purpose.
 *
 * KEEP — the league's shape, because prices only mean something against it:
 *   teamCount, startingBudget, roster slot counts, scoring, superflex/PPR.
 *   Per season: year, and per pick name / pos / amount / keeper / pickNo /
 *   seatIdx.
 *
 * STRIP — never emitted, at any nesting depth:
 *   league name and id, members (displayNames, userIds, avatars), pickedBy,
 *   rosterId, draftSlot, importedByUsername, importedByUserId, any customName.
 *   Also draftId, which is a live handle back to the source league.
 *
 * seatIdx is the deliberate compromise. Concentration metrics ("one manager
 * bought 40% of the RB dollars") need picks grouped by buyer, but the buyer's
 * identity is exactly what must not travel. So each season gets a dense
 * 0..n-1 index assigned by FIRST APPEARANCE of the original manager key, and
 * that key is discarded. The indices are deliberately NOT stable across
 * seasons or across leagues: seat 3 in 2024 and seat 3 in 2023 are unrelated,
 * which is what stops the payload from being a longitudinal profile of a
 * person. Don't "fix" that — it's the privacy property, not a bug.
 *
 * @param {object} league — a saved league record
 * @returns {{payloadVersion: 1, shape: object, seasons: Array}|null}
 */

// Manager key candidates, best first. pickedBy is the Sleeper user id, which
// is the only one that identifies the same person across drafts; rosterId and
// draftSlot are league-local seat numbers. All three are hashed away into
// seatIdx and never emitted — the preference order only affects how accurately
// picks group within one season.
const SEAT_KEY_FIELDS = ["pickedBy", "rosterId", "draftSlot"];

const present = (v) => v !== null && v !== undefined && v !== "";

// A whole season commits to ONE key field, chosen by the first that any pick
// carries. Mixing fields within a season would split one manager across two
// seats (a pick with pickedBy and a pick with only rosterId look like
// different buyers), which silently corrupts the very metrics seatIdx exists
// to support. Picks missing the chosen field get seatIdx null.
function seatKeyField(picks) {
  for (const f of SEAT_KEY_FIELDS) {
    if (picks.some(p => p && present(p[f]))) return f;
  }
  return null;
}

// Copy only the numeric entries of a settings object. Every real key in
// `positions` and `scoring` is a number, so this is lossless for valid input —
// and it means a string that somehow landed in a settings blob (a team name, a
// note, anything identifying) cannot ride out inside an object we intended to
// pass through wholesale.
function numericEntries(obj) {
  const out = {};
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    const n = Number(v);
    if (typeof v !== "boolean" && v !== "" && Number.isFinite(n)) out[k] = n;
  }
  return out;
}

const finiteOrNull = (v) => {
  if (!present(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function anonymizeAuctionHistory(league) {
  const history = league && Array.isArray(league.auctionHistory) ? league.auctionHistory : null;
  // No history means there is nothing to describe. An empty array counts as
  // none — callers get one falsy answer to check instead of two.
  if (!history || !history.length) return null;

  const positions = numericEntries(league.positions);
  const scoring = numericEntries(league.scoring);

  const shape = {
    teamCount: finiteOrNull(league.teams),
    startingBudget: finiteOrNull(league.budget),
    positions,
    scoring,
    // Derived conveniences so a reader doesn't have to know our settings keys.
    // Both are redundant with the two objects above, by design.
    superflex: (positions.SFLEX || 0) > 0,
    ppr: scoring.receptions ?? 0,
  };

  const seasons = history.map((season) => {
    const rawPicks = Array.isArray(season?.picks) ? season.picks : [];
    const keyField = seatKeyField(rawPicks);
    // Per season, so the numbering restarts every year. See the header.
    const seatByKey = new Map();

    const picks = rawPicks.map((p) => {
      let seatIdx = null;
      if (keyField && p && present(p[keyField])) {
        const key = String(p[keyField]);
        if (!seatByKey.has(key)) seatByKey.set(key, seatByKey.size);
        seatIdx = seatByKey.get(key);
      }
      return {
        // Player identity, not manager identity — this is the market datum.
        name: typeof p?.name === "string" ? p.name : "",
        pos: typeof p?.pos === "string" ? p.pos : null,
        amount: finiteOrNull(p?.amount),
        // Keepers are carried contracts, not market prices. Flagged rather
        // than dropped so a reader can exclude them and still see the draft.
        keeper: p?.keeper === true,
        pickNo: finiteOrNull(p?.pickNo),
        seatIdx,
      };
    });

    return { year: finiteOrNull(season?.season ?? season?.year), picks };
  });

  return { payloadVersion: 1, shape, seasons };
}

export default anonymizeAuctionHistory;
